import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { SubscriptionBanner } from '../components/SubscriptionBanner';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { useT, translateWorkType } from '../lib/i18n';
import { Plus, AlertTriangle, ArrowRight, FileText, CheckCircle2, Clock, DollarSign, Users, Truck, Sparkles, ClipboardList, UserPlus, Receipt } from 'lucide-react';
import { displayNumber, isNumberPending } from '../lib/numbering';

const money = (n) => (Number(n) || 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

export default function Dashboard() {
  const { user } = useAuth();
  const { submissions, profile } = useData();
  const { t, lang, setLang } = useT();

  const stats = useMemo(() => {
    const total = submissions.length;
    const accepted = submissions.filter(s => s.status === 'accepted');
    const pending = submissions.filter(s => s.status === 'pending').length;
    const revenue = accepted.reduce((s, sub) => s + (Number(sub.summary?.grandTotal) || 0), 0);
    return { total, accepted: accepted.length, pending, revenue };
  }, [submissions]);

  const expiringSoon = useMemo(() => {
    const now = Date.now();
    const threshold = now + 3 * 86400000;
    return submissions.filter(s => {
      if (!s.expires_at || ['accepted', 'refused', 'expired'].includes(s.status)) return false;
      const t = new Date(s.expires_at).getTime();
      return t > now && t < threshold;
    });
  }, [submissions]);

  const recent = submissions.slice(0, 5);
  const hello = profile?.company_name || (user?.email || '').split('@')[0];

  return (
    <Layout>
      <SubscriptionBanner />
      <section className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">
          {t('dash.hello')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
          {hello}.
        </h1>
      </section>

      {expiringSoon.length > 0 && (
        <div
          data-testid="expiring-alert"
          className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border-2 border-amber-300"
        >
          <AlertTriangle className="text-amber-700 shrink-0 mt-0.5" size={20} />
          <div className="flex-1 text-sm">
            <div className="font-bold text-amber-900">
              {expiringSoon.length} {t('dash.expiring')}
            </div>
            <Link to="/submissions" className="text-amber-800 underline font-semibold">
              {t('hist.view')} →
            </Link>
          </div>
        </div>
      )}

      <Link
        to="/submissions/new"
        data-testid="new-submission-btn"
        className="btn-accent w-full gap-2 mb-4 text-lg"
      >
        <Plus size={22} /> {t('dash.new_submission')}
      </Link>

      <section className="grid grid-cols-2 gap-3 mb-8" data-testid="quick-access-tiles">
        <Link
          to="/clients"
          data-testid="tile-clients"
          className="card p-4 flex flex-col items-start gap-2 hover:border-[#7A1F2B] hover:shadow-md transition-all"
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: '#FAF7F2', color: '#7A1F2B' }}
          >
            <Users size={20} strokeWidth={2.2} />
          </div>
          <div className="font-heading font-black text-lg leading-tight" style={{ color: '#5A1620' }}>
            {t('dash.tile.clients')}
          </div>
          <div className="text-xs text-gray-500">{t('dash.tile.clients_sub')}</div>
        </Link>

        <Link
          to="/invoices"
          data-testid="tile-invoices"
          className="card p-4 flex flex-col items-start gap-2 hover:shadow-md transition-all relative overflow-hidden"
          style={{ borderColor: '#C9A961', borderWidth: '2px' }}
        >
          <div
            className="absolute top-2 right-2 text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
            style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
          >
            <Sparkles size={9} /> {t('dash.tile.new_badge')}
          </div>
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: '#5A1620', color: '#C9A961' }}
          >
            <Receipt size={20} strokeWidth={2.2} />
          </div>
          <div className="font-heading font-black text-lg leading-tight" style={{ color: '#5A1620' }}>
            {t('dash.tile.invoices')}
          </div>
          <div className="text-xs text-gray-500">{t('dash.tile.invoices_sub')}</div>
        </Link>

        <Link
          to="/suppliers"
          data-testid="tile-suppliers"
          className="card p-4 flex flex-col items-start gap-2 hover:shadow-md transition-all relative overflow-hidden"
          style={{ borderColor: '#C9A961', borderWidth: '2px' }}
        >
          <div
            className="absolute top-2 right-2 text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
            style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
          >
            <Sparkles size={9} /> {t('dash.tile.new_badge')}
          </div>
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: '#5A1620', color: '#C9A961' }}
          >
            <Truck size={20} strokeWidth={2.2} />
          </div>
          <div className="font-heading font-black text-lg leading-tight" style={{ color: '#5A1620' }}>
            {t('dash.tile.suppliers')}
          </div>
          <div className="text-xs text-gray-500">{t('dash.tile.suppliers_sub')}</div>
        </Link>

        <Link
          to="/templates"
          data-testid="tile-templates"
          className="card p-4 flex flex-col items-start gap-2 hover:shadow-md transition-all relative overflow-hidden col-span-2"
          style={{ borderColor: '#C9A961', borderWidth: '2px' }}
        >
          <div
            className="absolute top-2 right-2 text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
            style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
          >
            <Sparkles size={9} /> {t('dash.tile.new_badge')}
          </div>
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: '#FAF7F2', color: '#7A1F2B' }}
          >
            <ClipboardList size={20} strokeWidth={2.2} />
          </div>
          <div className="font-heading font-black text-lg leading-tight" style={{ color: '#5A1620' }}>
            {t('dash.tile.templates')}
          </div>
          <div className="text-xs text-gray-500">{t('dash.tile.templates_sub')}</div>
        </Link>

        <Link
          to="/team"
          data-testid="tile-team"
          className="card p-4 flex flex-col items-start gap-2 hover:shadow-md transition-all relative overflow-hidden col-span-2"
          style={{ background: 'linear-gradient(135deg,#7A1F2B 0%,#5A1620 100%)', borderColor: '#C9A961', borderWidth: '2px' }}
        >
          <div
            className="absolute top-2 right-2 text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full inline-flex items-center gap-1"
            style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
          >
            <Sparkles size={9} /> {t('dash.tile.new_badge')}
          </div>
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: '#C9A961', color: '#5A1620' }}
          >
            <UserPlus size={20} strokeWidth={2.4} />
          </div>
          <div className="font-heading font-black text-lg leading-tight" style={{ color: '#FAF7F2' }}>
            {t('dash.tile.team')}
          </div>
          <div className="text-xs" style={{ color: '#E5DCC9' }}>
            {t('dash.tile.team_sub')}
          </div>
        </Link>
      </section>

      <section className="grid grid-cols-2 gap-3 mb-10">
        <StatCard icon={FileText} label={t('dash.stats.total')} value={stats.total} testid="stat-total" />
        <StatCard icon={CheckCircle2} label={t('dash.stats.accepted')} value={stats.accepted} testid="stat-accepted" color="text-emerald-700" />
        <StatCard icon={Clock} label={t('dash.stats.pending')} value={stats.pending} testid="stat-pending" color="text-amber-700" />
        <StatCard icon={DollarSign} label={t('dash.stats.revenue')} value={money(stats.revenue)} testid="stat-revenue" small />
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">{t('dash.recent')}</h2>
          <Link to="/submissions" className="text-sm font-bold text-[#7A1F2B] inline-flex items-center gap-1">
            {t('hist.view')} <ArrowRight size={14} />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="card p-8 text-center text-gray-500" data-testid="empty-state">
            {t('dash.empty')}
          </div>
        ) : (
          <div className="space-y-3">
            {recent.map((s) => (
              <Link
                key={s.id}
                to={`/submissions/${s.id}`}
                data-testid={`submission-row-${s.id}`}
                className="card p-4 flex items-center justify-between gap-3 hover:border-[#7A1F2B] transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-mono text-xs ${isNumberPending(s.submission_number) ? 'italic text-amber-600' : 'text-gray-500'}`}>
                      {displayNumber(s.submission_number, t, { short: true })}
                    </span>
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="font-bold truncate">{s.client_name || '—'}</div>
                  <div className="text-xs text-gray-500 truncate">{translateWorkType(s.work_type, lang)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading font-black text-lg">{money(s.summary?.grandTotal)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <footer className="mt-12 pt-6 border-t border-[#E5DCC9] text-center" data-testid="dashboard-footer">
        <div className="text-[11px] text-gray-400 space-x-3">
          <Link to="/support" className="hover:text-[#7A1F2B] hover:underline" data-testid="dashboard-footer-support-link">
            {lang === 'fr' ? 'Aide & Support' : 'Help & Support'}
          </Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-[#7A1F2B] hover:underline">
            {lang === 'fr' ? 'Conditions' : 'Terms'}
          </Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-[#7A1F2B] hover:underline">
            {lang === 'fr' ? 'Confidentialité' : 'Privacy'}
          </Link>
        </div>
        <div className="mt-3 inline-flex items-center gap-1 rounded-full border border-[#E5DCC9] bg-white p-0.5" data-testid="dashboard-footer-lang-toggle">
          <button
            type="button"
            onClick={() => setLang('fr')}
            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest transition-colors ${lang === 'fr' ? 'bg-[#7A1F2B] text-white' : 'text-gray-500 hover:text-[#7A1F2B]'}`}
            data-testid="dashboard-footer-lang-fr"
            aria-pressed={lang === 'fr'}
          >
            FR
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-widest transition-colors ${lang === 'en' ? 'bg-[#7A1F2B] text-white' : 'text-gray-500 hover:text-[#7A1F2B]'}`}
            data-testid="dashboard-footer-lang-en"
            aria-pressed={lang === 'en'}
          >
            EN
          </button>
        </div>
      </footer>
    </Layout>
  );
}

function StatCard({ icon: Icon, label, value, testid, color = 'text-[#7A1F2B]', small }) {
  return (
    <div data-testid={testid} className="card p-4">
      <Icon className={`${color} mb-2`} size={18} strokeWidth={2.2} />
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-heading font-black tracking-tighter mt-1 ${small ? 'text-xl' : 'text-3xl'} ${color}`}>{value}</div>
    </div>
  );
}
